import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Action Items (z8nrz7fkfh): follow-ups people raise on a form or any record,
 * assigned to a person or a group.
 */

export type ActionItemStatus = 'OPEN' | 'ASSIGNED' | 'STARTED' | 'COMPLETED'
export type ActionItemPriority = 'LOW' | 'MED' | 'HIGH'
export type ActionItemSource = 'FORM' | 'INVENTORY' | 'EQUIPMENT' | 'KIT' | 'GENERAL'

export interface ActionItemAttachment {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  uploadedBy: string
  uploadedAt: string
}

export interface ActionItem {
  id: string
  /** The A#. */
  number: number
  description: string
  priority: ActionItemPriority
  /** YYYY-MM-DD. */
  dueDate: string
  comments: string | null
  status: ActionItemStatus
  assignedUser: { id: string; name: string } | null
  assignedGroup: { id: string; name: string } | null
  sourceType: ActionItemSource
  /** The submission, material, unit or kit build; null for General. */
  sourceId: string | null
  /** What the Source cell says: MES, Transplant, Equipment, General… */
  sourceArea: string
  /** The form, unit, material or kit by name. */
  sourceLabel: string | null
  /** Forms only: the run the form was open in. */
  sourcePath: string | null
  createdById: string | null
  createdByName: string
  createdAt: string
  updatedAt: string
  startedAt: string | null
  completedAt: string | null
  attachments: ActionItemAttachment[]
  canEdit: boolean
  canRemove: boolean
}

export interface ActionItemFilters {
  groupId?: string
  createdById?: string
  assignedUserId?: string
  statuses?: ActionItemStatus[]
  /** Past Due & This Week. */
  due?: 'this_week'
  /** Raised on this record: a submission id for a form. */
  sourceId?: string
}

export interface CreateActionItemInput {
  description: string
  assignedUserId?: string
  assignedGroupId?: string
  priority: ActionItemPriority
  dueDate: string
  comments?: string
  sourceType: ActionItemSource
  sourceId?: string
  sourcePath?: string
}

export interface UpdateActionItemInput {
  description?: string
  assignedUserId?: string | null
  assignedGroupId?: string | null
  priority?: ActionItemPriority
  dueDate?: string
  comments?: string | null
  status?: ActionItemStatus
}

export interface ActionItemAssignees {
  users: { id: string; name: string; email: string }[]
  groups: { id: string; name: string }[]
}

interface PresignedUpload {
  url: string
  method: 'PUT'
  headers: Record<string, string>
  storageKey: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `headers` spread LAST, as in attachmentsApi.ts: letting `...init` clobber
  // them silently drops the bearer token.
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

const json = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

function filterQuery(filters: ActionItemFilters): string {
  const params = new URLSearchParams()
  if (filters.groupId) params.set('groupId', filters.groupId)
  if (filters.createdById) params.set('createdById', filters.createdById)
  if (filters.assignedUserId) params.set('assignedUserId', filters.assignedUserId)
  if (filters.statuses?.length) params.set('statuses', filters.statuses.join(','))
  if (filters.due) params.set('due', filters.due)
  if (filters.sourceId) params.set('sourceId', filters.sourceId)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const actionItemsApi = {
  list: (filters: ActionItemFilters = {}) => request<ActionItem[]>(`/action-items${filterQuery(filters)}`),

  openCount: (submissionId: string) =>
    request<{ count: number }>(`/action-items/open-count?submissionId=${encodeURIComponent(submissionId)}`),

  assignees: () => request<ActionItemAssignees>('/action-items/assignees'),

  create: (input: CreateActionItemInput) =>
    request<ActionItem>('/action-items', { method: 'POST', ...json(input) }),

  update: (id: string, input: UpdateActionItemInput) =>
    request<ActionItem>(`/action-items/${id}`, { method: 'PATCH', ...json(input) }),

  remove: (id: string) => request<{ ok: true }>(`/action-items/${id}`, { method: 'DELETE' }),

  /**
   * Presign → PUT the bytes straight to storage → confirm, as for equipment
   * documents. The PUT is a bare `fetch`: the presigned URL carries its own
   * signature, and an Authorization header beside it makes S3 refuse it.
   */
  async attach(id: string, file: File): Promise<ActionItemAttachment> {
    const presigned = await request<PresignedUpload>(`/action-items/${id}/attachments/presign`, {
      method: 'POST',
      ...json({ fileName: file.name, mimeType: file.type, sizeBytes: file.size }),
    })
    const put = await fetch(presigned.url, { method: presigned.method, headers: presigned.headers, body: file })
    if (!put.ok) throw new Error(`Upload failed (${put.status}). The file was not saved.`)
    return request<ActionItemAttachment>(`/action-items/${id}/attachments`, {
      method: 'POST',
      ...json({
        storageKey: presigned.storageKey,
        fileName: presigned.fileName,
        mimeType: presigned.mimeType,
        sizeBytes: presigned.sizeBytes,
      }),
    })
  },

  downloadUrl: (attachmentId: string) =>
    request<{ url: string; fileName: string }>(`/action-items/attachments/${attachmentId}/download`),
}
